import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { t } from '../i18n/t';
import { ToastService } from '../services/toast.service';
import { AuthApi, type SignInCredentials } from './auth-api';
import { InMemoryAuthApi } from './in-memory-auth-api';
import { SessionStore } from './session.store';

export const SIGN_IN_PATH = '/sign-in';

/**
 * Orchestrates sign-in, restore, refresh and sign-out over `AuthApi` and keeps `SessionStore`
 * in step. Components and guards call this; nothing else writes the store.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(AuthApi);
  private readonly store = inject(SessionStore);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  private restoring: Promise<void> | null = null;
  private refreshing: Promise<boolean> | null = null;

  /**
   * Resolves the session from the cookie once. Later calls share the first answer, so a
   * burst of guarded navigations makes one request. Any failure leaves the user anonymous;
   * the guard decides what that means for the route.
   */
  restore(): Promise<void> {
    if (this.store.status() !== 'unknown') return Promise.resolve();
    this.restoring ??= firstValueFrom(this.api.me()).then(
      (snapshot) => this.store.set(snapshot),
      () => this.store.clear(),
    );
    return this.restoring;
  }

  /** Signs in. Rejects with an `ApiError`; the caller shows `userMessage` on the form. */
  async signIn(credentials: SignInCredentials): Promise<void> {
    this.store.set(await firstValueFrom(this.api.signIn(credentials)));
  }

  /**
   * Changes the password, then re-reads the session so a cleared must-change flag takes effect.
   * Rejects with an `ApiError` (`auth.password_weak`, validation errors) for the form to show.
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await firstValueFrom(this.api.changePassword(currentPassword, newPassword));
    this.store.set(await firstValueFrom(this.api.me()));
  }

  /** Asks for a reset link. Resolves the same way for an unknown login. */
  forgotPassword(login: string): Promise<void> {
    return firstValueFrom(this.api.forgotPassword(login));
  }

  /** Sets a new password from a reset link. Rejects with `auth.reset_token_invalid`. */
  resetPassword(token: string, newPassword: string): Promise<void> {
    return firstValueFrom(this.api.resetPassword(token, newPassword));
  }

  /**
   * Mock only: switches the signed-in persona so the nav and guards re-evaluate. Resolves false
   * when the real API is in use, which has no such thing.
   */
  async viewAs(key: string): Promise<boolean> {
    if (!(this.api instanceof InMemoryAuthApi)) return false;
    this.store.set(await firstValueFrom(this.api.viewAs(key)));
    return true;
  }

  /** Signs out. The local session is cleared even when the server call fails. */
  async signOut(): Promise<void> {
    try {
      await firstValueFrom(this.api.signOut());
    } catch {
      // The cookies may already be gone. The local session must end either way.
    } finally {
      this.store.clear();
      this.restoring = null;
    }
    await this.router.navigate([SIGN_IN_PATH]);
  }

  /**
   * Asks the server to rotate the session cookies. Concurrent callers share one request, so
   * five calls that all got a 401 produce one refresh. Resolves false when it was refused.
   */
  refresh(): Promise<boolean> {
    this.refreshing ??= firstValueFrom(this.api.refresh()).then(
      () => true,
      () => false,
    );
    const current = this.refreshing;
    void current.finally(() => {
      if (this.refreshing === current) this.refreshing = null;
    });
    return current;
  }

  /**
   * A live session ended and could not be refreshed. Clears it, says so, and sends the user
   * to sign in, then back to the page they were on.
   */
  sessionEnded(): void {
    if (this.store.status() !== 'authenticated') return;
    this.store.clear();
    this.toast.show(t('error.sessionEnded'), { tone: 'warning' });
    const nav = this.router.getCurrentNavigation();
    const returnUrl = nav
      ? this.router.serializeUrl(nav.finalUrl ?? nav.extractedUrl)
      : this.router.url;
    void this.router.navigate([SIGN_IN_PATH], {
      queryParams: returnUrl && returnUrl !== '/' ? { returnUrl } : {},
    });
  }
}
